/**
 * ProjectsPage - Projects list and management
 *
 * Features:
 * - Project summary cards
 * - Project grid with status badges
 * - Create/Edit/Delete modals
 * - Real API integration
 */

import { Plus, RefreshCw } from 'lucide-react';
import { ProjectStatuses } from '@resolve-ai/shared';
import { Dropdown, LoadingState, Toast } from '@/components/common';
import { ProjectGrid, CreateProjectModal, EditProjectModal } from '@/components/projects';
import { useProjectsPage } from '../hooks/useProjectsPage';

export function ProjectsPage() {
  const { query, modal, form, actions, mutations, toast } = useProjectsPage();

  if (query.hasError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-800 dark:text-red-200">Failed to load projects</p>
      </div>
    );
  }

  const { active: activeCount, inactive: inactiveCount, archived: archivedCount } = query.projectCounts;

  if(query.isLoading){
      return <LoadingState message="Loading projects..." isLoading={query.isLoading} />
  }
  return (
    <div className="space-y-6">
      {/* Header with Inline Stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-subtitle text-[#1E1E1E] dark:text-white">Projects</h2>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {/* Active count */}
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <span className="circle bg-success-DEFAULT" data-size="sm" />
              <span className="font-caption text-info dark:text-white">{`${activeCount} Active`}</span>
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            {/* Inactive count */}
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <span className="circle bg-warning-DEFAULT" data-size="sm" />
              <span className="font-caption text-info dark:text-white">{`${inactiveCount} Inactive`}</span>
            </span>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            {/* Archived count */}
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <span className="circle bg-neutral" data-size="sm" />
              <span className="font-caption text-info dark:text-white">{`${archivedCount} Archived`}</span>
            </span>
          </div>
        </div>
        {/* Filter and New Project button */}
        <div>
          {/* Status Filter Dropdown */}
          <div className="flex flex-row items-center justify-end gap-3 mb-3">
            <div
              hidden={
                query.projectCounts.active === 0 &&
                query.projectCounts.inactive === 0 &&
                query.projectCounts.archived === 0
              }
              className="w-40"
            >
              <Dropdown
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Active', value: ProjectStatuses.ACTIVE },
                  { label: 'Inactive', value: ProjectStatuses.INACTIVE },
                  { label: 'Archived', value: ProjectStatuses.ARCHIVED },
                ]}
                value={query.statusFilter}
                onChange={(value) => query.setStatusFilter(value as 'all' | 'active' | 'inactive' | 'archived')}
              />
            </div>
            <button
              hidden={
                (query.statusFilter === 'all' &&
                  query.projectCounts.active === 0 &&
                  query.projectCounts.inactive === 0) ||
                (query.statusFilter === 'active' && query.projectCounts.active === 0)
              }
              onClick={actions.openCreateModal}
              disabled={
                !query.canCreateProject ||
                query.isLoadingIntegrations ||
                query.isLoadingRepos ||
                mutations.isCreating
              }
              className="flex items-center leading-normal text-[1rem] gap-2 p-3 text-white disabled:text-black/50 rounded-lg transition-colors bg-primary-DEFAULT disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
              title={!query.canCreateProject ? 'Connect GitHub and Jira in Settings first' : undefined}
            >
              {query.isLoadingRepos ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              New Project
            </button>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <ProjectGrid
        projects={query.projects}
        totalCount={query.totalCount}
        statusFilter={query.statusFilter}
        canCreateProject={query.canCreateProject}
        isLoadingIntegrations={query.isLoadingIntegrations}
        isLoadingRepos={query.isLoadingRepos}
        openMenuId={query.openMenuId}
        onToggleMenu={actions.toggleMenu}
        onEditClick={actions.openEditModal}
        onDeleteClick={actions.openDeleteModal}
        onOpenCreateModal={actions.openCreateModal}
      />

      {/* Create Modal */}
      <CreateProjectModal
        isOpen={modal.active === 'create'}
        onClose={modal.close}
        onCreate={actions.createProject}
        form={form.create.data}
        onFormChange={form.create.setData}
        repositories={query.repositories}
        jiraProjects={query.jiraProjects}
        isFormValid={form.create.isValid}
        isSaving={mutations.isCreating}
      />

      {/* Edit Modal */}
      <EditProjectModal
        isOpen={modal.active === 'edit'}
        onClose={modal.close}
        onUpdate={actions.updateProject}
        project={modal.active === 'edit' ? modal.selectedProject : null}
        form={form.edit.data}
        onFormChange={form.edit.setData}
        isSaving={mutations.isUpdating}
      />

      {/* Delete Confirmation Modal */}
      {modal.active === 'delete' && modal.selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Project</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <strong>{modal.selectedProject.name}</strong>? This action cannot be
              undone.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={actions.deleteProject}
                disabled={mutations.isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors"
              >
                {mutations.isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={modal.close}
                disabled={mutations.isDeleting}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        isOpen={toast.state.isOpen}
        onClose={toast.close}
        message={toast.state.message}
        variant={toast.state.variant}
      />
    </div>
  );
}
